import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button } from '@/design-system';
import { landingPalette } from '@/features/variants/api';
import { diffQuery, type RevisionOut } from './api';
import type { Device } from './bridge';
import type { DesignerCtx } from './DesignerTab';
import { PreviewFrame } from './PreviewFrame';
import { StandInPreview } from './StandInPreview';
import s from './panels.module.css';

type Props = {
  ctx: DesignerCtx;
  from: RevisionOut;
  to: RevisionOut;
  device: Device;
  products: { name: string; price: number | null }[];
  onClose: () => void;
};

const NO_PICK = { section: null, el: null };
const STATUSES = ['added', 'modified', 'removed'] as const;

/**
 * «Сравнить» (additive, DESIGN_SPEC §9 C5): the chosen revision and the current one side by side in the
 * preview frame, each from its own preview build, with the files that differ — both from
 * `GET /admin/revisions/{a}/diff/{b}` (`files`, `a_preview`, `b_preview`).
 */
export function ComparePane({ ctx, from, to, device, products, onClose }: Props) {
  const { t } = useTranslation('designer');
  const diff = useQuery(diffQuery(from.id, to.id)).data;
  const panes = [
    { id: from.id, label: t('compare.left', { n: from.number }), url: diff?.a_preview.preview_url ?? null },
    { id: to.id, label: t('compare.right', { n: to.number }), url: diff?.b_preview.preview_url ?? null },
  ];
  return (
    <div className={s.compare} role="region" aria-label={t('compare.title', { a: from.number, b: to.number })}>
      <div className={s.compareHead}>
        <h3 className={s.compareTitle}>{t('compare.title', { a: from.number, b: to.number })}</h3>
        <div className={s.files} aria-label={t('compare.files')}>
          {diff && !diff.files.length && <span>{t('compare.none')}</span>}
          {diff?.files.map((f) => {
            const status = STATUSES.find((x) => x === f.status) ?? 'modified';
            return (
              <span key={f.path} className={s.file} title={f.truncated ? undefined : f.diff}>
                {f.path} · {t(`compare.status.${status}`)} · {t('compare.lines', { add: f.additions, del: f.deletions })}
              </span>
            );
          })}
        </div>
        <Button variant="secondary" size={32} icon="x" iconSize={13} className={s.closeCompare} onClick={onClose}>
          {t('compare.close')}
        </Button>
      </div>
      <div className={s.panes}>
        {panes.map((p) => (
          <div key={p.id} className={s.pane}>
            <span className={s.paneLabel}>{p.label}</span>
            {p.url ? (
              <PreviewFrame url={p.url} device={device} pick={false} selection={NO_PICK} onSelect={() => undefined} label={p.label} />
            ) : (
              <StandInPreview
                palette={landingPalette(ctx.variant)}
                brand={ctx.site.organization.brand_name}
                headline={t('stand_in.headline')}
                products={products}
                device={device === 'desktop' ? 'tablet' : device}
                pick={false}
                selection={NO_PICK}
                onSelect={() => undefined}
                label={p.label}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
